
import { useState, useCallback } from 'react';
import { useApi } from '@hooks/useApi';

export function useFileUpload(isMeter = true, options?: {
  isApproved?: boolean;
  isEffectiveMonth?: boolean;
}) {
  const [uploading, setUploading] = useState(false);
  const { uploadMeterFiles, uploadSCADAFiles } = useApi();
  const [success, setSuccess] = useState<boolean | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [batchId, setBatchId] = useState<string | null>(null);

  const uploadFiles = useCallback(
    async (files: File[], params = {}) => {
      setUploading(true);
      setSuccess(null);
      setError(null);
      setRunId(null);
      setBatchId(null);

      try {
        const response = await (isMeter
          ? uploadMeterFiles(files)
          : uploadSCADAFiles(files, params));

        // Check if the response indicates a validation failure
        if (response.success === false) {
          setUploading(false);
          setSuccess(false);
          // Return the full response so errors can be extracted
          return response;
        }

        // Meter uploads no longer trigger the ADF pipeline (and get a runId) synchronously —
        // the pipeline only starts once the malware-scan webhook confirms the batch is Clean.
        // Callers should poll scan-status with batchId to discover the runId once available.
        const pipelineRunId = response.data?.pipeline?.runId ?? null;
        const uploadBatchId = response.data?.batchId ?? null;
        setRunId(pipelineRunId);
        setBatchId(uploadBatchId);

        setUploading(false);
        setSuccess(true);
        return {
          success: true,
          data: response.data,
          runId: pipelineRunId,
          batchId: uploadBatchId,
        };
      } catch (err: any) {
        console.error("Upload error:", err);

        let uiMessage = "File upload failed";

        try {
          // Try to parse the error message for structured error info
          const errorJson = JSON.parse(
            err.message.replace(/^Upload failed: \d+\s*-\s*/, "")
          );

          if (errorJson.errors?.length) {
            uiMessage = errorJson.errors.join("\n");
          } else if (errorJson.message) {
            uiMessage = errorJson.message;
          } else if (errorJson.error) {
            uiMessage = errorJson.error;
          }
        } catch (e) {
          console.error("Failed parsing server response", e);
        }


        setError(uiMessage);
        setUploading(false);
        setSuccess(false);

        return {
          success: false,
          message: uiMessage
        };
      }
    },
    [isMeter, uploadMeterFiles, uploadSCADAFiles]
  );

  const uploadFile = useCallback(
    async (file: File, params = {}) => {
      const isApproved = options?.isApproved ?? false;
      const isEffectiveMonth = options?.isEffectiveMonth ?? false;

      if (isApproved && isEffectiveMonth) {
        setError('Cannot upload files for approved effective month');
        setSuccess(false);
        return {
          success: false,
          message: 'Cannot upload files for approved effective month'
        };
      }

      return uploadFiles([file], params);
    },
    [uploadFiles, options]
  );

  return { uploading, uploadFiles, uploadFile, error, success, runId, batchId };
}